import { cn } from '@/lib/utils';
import { LoadingPage } from '@/libs/components/src/lib/LoadingPage';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@chiroup/components/ui/dialog';
import React, { use, useEffect } from 'react';
import { ToastContext } from '../../contexts/toast.context';
import Tabs, { Tab } from '../layout/Tabs';
import FooterButtons, { ModalActionButton } from './FooterButtons';

export type { ModalActionButton };

type Props = {
  isOpen: boolean;
  children?: React.ReactNode | React.ReactNode[];
  close?: () => void;
  className?: string;
  addClassName?: string;
  disableClickOutsideClose?: boolean;
  isFullScreen?: boolean;
  header?:
    | React.ReactNode
    | {
        title?: string;
        description?: string | React.ReactNode;
        icon?: React.ComponentType<{ className?: string }>;
      };
  footer?:
    | React.ReactNode
    | {
        buttons?: (ModalActionButton | React.ReactNode)[];
        sticky?: boolean;
        leftSide?: React.ReactNode;
      };
  loading?: boolean;
  size?: 'xs' | 'sm' | 'default' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full' | 'auto';
  darkBg?: boolean;
  tabs?: Tab[];
  handleChangeTab?: (e: React.ChangeEvent<HTMLSelectElement>, idx?: number) => void;
  showCloseButton?: boolean;
  contentClassName?: string;
  onOpenAutoFocus?: (e: Event) => void;
  // Ref to the scrollable body container (accepts callback refs, e.g. from
  // useModalTouchScroll for iOS touch-scroll support).
  scrollRef?: React.Ref<HTMLDivElement> | null;
};

const Modal: React.FC<Props> = ({
  isOpen,
  children,
  close,
  className = '',
  addClassName = '',
  disableClickOutsideClose = false,
  isFullScreen = false,
  header,
  footer,
  loading = false,
  size,
  darkBg = false,
  tabs = [],
  handleChangeTab,
  showCloseButton = true,
  contentClassName,
  onOpenAutoFocus,
  scrollRef,
}) => {
  // Determine the dialog size based on props
  const dialogSize = size || (isFullScreen ? 'full' : 'default');

  // Only use custom positioning classes for legacy support when no size is specified
  // and the dialog should use completely custom styling
  const customClassName = className;

  const { isToast } = use(ToastContext);

  const closeModal = (open: boolean) => {
    if (!open) {
      close?.();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body?.classList.add('overflow-y-hidden');
    }

    return () => {
      document.body?.classList.remove('overflow-y-hidden');
    };
  }, [isOpen]);

  // Check if header is an object with title/description or a ReactNode
  const isHeaderObject =
    header &&
    typeof header === 'object' &&
    !React.isValidElement(header) &&
    ('title' in header || 'description' in header || 'icon' in header);
  const headerTitle = isHeaderObject
    ? (
        header as {
          title?: string;
          description?: string;
          icon?: React.ComponentType<{ className?: string }>;
        }
      ).title
    : undefined;
  const headerDescription = isHeaderObject
    ? (
        header as {
          title?: string;
          description?: string;
          icon?: React.ComponentType<{ className?: string }>;
        }
      ).description
    : undefined;
  const HeaderIcon = isHeaderObject
    ? (
        header as {
          title?: string;
          description?: string;
          icon?: React.ComponentType<{ className?: string }>;
        }
      ).icon
    : undefined;

  // Check if footer is an object with buttons or a ReactNode
  const isFooterObject =
    footer &&
    typeof footer === 'object' &&
    !React.isValidElement(footer) &&
    ('buttons' in footer || 'sticky' in footer || 'leftSide' in footer);
  const footerButtons = isFooterObject
    ? (footer as { buttons?: ModalActionButton[]; sticky?: boolean }).buttons
    : undefined;

  return (
    <Dialog open={isOpen} onOpenChange={closeModal} modal>
      {loading ? (
        <DialogContent
          size={dialogSize}
          className={cn(
            customClassName,
            addClassName,
            dialogSize !== 'full' && 'flex flex-col max-h-[90vh]',
            darkBg ? 'bg-gray-50 dark:bg-dark-gray-700' : 'dark:bg-dark-gray-900'
          )}
        >
          <DialogTitle />
          <LoadingPage />
        </DialogContent>
      ) : (
        <DialogContent
          size={dialogSize}
          className={cn(
            customClassName,
            addClassName,
            dialogSize !== 'full' && 'flex flex-col max-h-[90vh]',
            darkBg ? 'bg-gray-50 dark:bg-dark-gray-700' : 'dark:bg-dark-gray-900',
            'print:!block print:!max-h-none print:!w-full print:!max-w-none print:!h-auto print:!transform-none print:!bg-white print:!shadow-none print:!border-none print:!rounded-none print:!p-0 print:!relative print:!inset-auto print:!translate-x-0 print:!translate-y-0'
          )}
          onPointerDownOutside={e => {
            if (disableClickOutsideClose || isToast) {
              e.preventDefault();
            }
          }}
          onEscapeKeyDown={e => {
            if (disableClickOutsideClose || isToast) {
              e.preventDefault();
            }
          }}
          showCloseButton={showCloseButton}
          onOpenAutoFocus={onOpenAutoFocus}
        >
          {header && isHeaderObject && (
            <DialogHeader
              className={cn(
                'flex-shrink-0 bg-white dark:bg-dark-gray-900',
                darkBg && 'border-b border-gray-300 dark:border-dark-gray-600',
                tabs?.length > 0 && 'pb-0',
                'print:!bg-transparent print:!border-none print:!shadow-none print:!p-0 print:!flex-shrink-0'
              )}
            >
              {HeaderIcon ? (
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30 flex-shrink-0">
                    <HeaderIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <DialogTitle>{headerTitle || ''}</DialogTitle>
                    {headerDescription && (
                      <DialogDescription>{headerDescription}</DialogDescription>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <DialogTitle>{headerTitle || ''}</DialogTitle>
                  {headerDescription && <DialogDescription>{headerDescription}</DialogDescription>}
                </>
              )}
              {tabs?.length > 0 && <Tabs tabs={tabs} onClick={handleChangeTab} />}
            </DialogHeader>
          )}
          {header && !isHeaderObject && (
            <div
              className={cn(
                'flex-shrink-0 bg-white dark:bg-dark-gray-900',
                darkBg && 'border-b border-gray-300 dark:border-dark-gray-600',
                'print:!bg-transparent print:!border-none print:!shadow-none print:!p-0 print:!flex-shrink-0'
              )}
            >
              {header as React.ReactNode}
            </div>
          )}
          {!header && (
            // radix cries if you don't have a title
            <DialogTitle />
          )}

          {footer ? (
            <>
              <div
                ref={scrollRef}
                className={cn(
                  'flex-1 min-h-0 overflow-y-auto print:overflow-y-visible print:!block print:!h-auto print:!min-h-0 print:!flex-initial print:!overflow-visible',
                  contentClassName
                )}
              >
                <div className={cn('px-6 pb-6 pt-4', 'print:!p-0')}>{children}</div>
              </div>

              {footer && isFooterObject && (
                <DialogFooter
                  className={cn(
                    'bg-white dark:bg-dark-gray-900 flex-shrink-0 flex justify-between',
                    darkBg && 'border-t border-gray-300 dark:border-dark-gray-600',
                    'print:!hidden'
                  )}
                >
                  <div className="flex items-center">{footer.leftSide}</div>
                  {footerButtons && <FooterButtons buttons={footerButtons} />}
                </DialogFooter>
              )}
              {footer && !isFooterObject && (
                <DialogFooter
                  className={cn(
                    'bg-white dark:bg-dark-gray-900 flex-shrink-0',
                    darkBg && 'border-t border-gray-300 dark:border-dark-gray-600',
                    'print:!hidden'
                  )}
                >
                  {footer as React.ReactNode}
                </DialogFooter>
              )}
            </>
          ) : (
            <div
              ref={scrollRef}
              className={cn(
                'flex-1 min-h-0 overflow-y-auto print:overflow-y-visible print:!block print:!h-auto print:!min-h-0 print:!flex-initial print:!overflow-visible',
                contentClassName
              )}
            >
              <div
                className={cn(
                  'px-6 pb-6 pt-4 h-full',
                  darkBg && 'bg-gray-50 dark:bg-dark-gray-900',
                  'print:!p-0 print:!bg-white print:!h-auto'
                )}
              >
                {children}
              </div>
            </div>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};

export default Modal;
